import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Clock, ChevronRight } from 'lucide-react';
import { API } from '@/App';

const FormationsPage = () => {
  const navigate = useNavigate();
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchFormations();
  }, []);

  const fetchFormations = async () => {
    try {
      const response = await axios.get(`${API}/formations`);
      setFormations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-testid="formations-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Formations Continue
          </h1>
          <p className="text-lg text-gray-600">
            Accédez à nos formations, replays et cours en ligne
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12" data-testid="loading">
            <div className="animate-pulse text-[#1e3a5f]">Chargement...</div>
          </div>
        ) : formations.length === 0 ? (
          <Card className="border-2 border-gray-100" data-testid="no-formations">
            <CardContent className="p-12 text-center">
              <BookOpen className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg">Aucune formation disponible pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {formations.map((formation, index) => (
              <Card
                key={formation.id}
                className="border-2 border-gray-100 card-hover cursor-pointer"
                onClick={() => navigate(`/formations/${formation.id}`)}
                data-testid={`formation-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center mb-4">
                    <BookOpen className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid={`formation-title-${index}`}>
                    {formation.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {formation.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <Clock className="mr-2" size={16} />
                    {new Date(formation.created_at).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">
                      {formation.category}
                    </span>
                    <Button variant="ghost" className="text-purple-600 hover:text-purple-700 p-0">
                      Voir <ChevronRight className="ml-1" size={20} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormationsPage;
